import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, Shield, Trash2, 
  Trophy, Heart, IndianRupee, Award, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ScoreCircle from '../components/ScoreCircle';
import GooglePlacesAutocomplete from '../components/GooglePlacesAutocomplete';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ComparePage = () => {
  const navigate = useNavigate();
  const [isSearching, setIsSearching] = useState(false);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  const handlePlaceSelect = async (placeDetails) => {
    if (selectedVenues.length >= 4) {
      toast.error('Maximum 4 venues can be compared');
      return;
    }

    setIsSearching(true);
    
    try {
      const response = await axios.post(`${API_URL}/venue/search`, {
        venue_name: placeDetails.name,
        location: placeDetails.city || placeDetails.address?.split(',')[1]?.trim() || null,
        place_id: placeDetails.place_id
      });

      if (response.data && response.data.id) {
        const newVenue = response.data;
        
        // Check for duplicates
        const isDuplicate = selectedVenues.some(v => v.id === newVenue.id || v.venue_name === newVenue.venue_name);
        if (isDuplicate) {
          toast.error('This venue is already added');
          return;
        }
        
        setSelectedVenues(prevVenues => {
          const updated = [...prevVenues, newVenue];
          console.log('Venues updated:', updated.length);
          return updated;
        });
        
        // Reset autocomplete
        setAutocompleteKey(prev => prev + 1);
        toast.success(`Added ${newVenue.venue_name}`);
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to fetch venue. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleManualEntry = async (venueName) => {
    if (selectedVenues.length >= 4) {
      toast.error('Maximum 4 venues can be compared');
      return;
    }

    if (!venueName.trim()) {
      toast.error('Please enter a venue name');
      return;
    }

    setIsSearching(true);
    
    try {
      const response = await axios.post(`${API_URL}/venue/search`, {
        venue_name: venueName.trim(),
        location: null
      });

      if (response.data && response.data.id) {
        const newVenue = response.data;
        
        // Check for duplicates
        const isDuplicate = selectedVenues.some(v => v.id === newVenue.id || v.venue_name === newVenue.venue_name);
        if (isDuplicate) {
          toast.error('This venue is already added');
          return;
        }
        
        setSelectedVenues(prevVenues => [...prevVenues, newVenue]);
        setAutocompleteKey(prev => prev + 1);
        toast.success(`Added ${newVenue.venue_name}`);
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to fetch venue. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRemoveVenue = (index) => {
    setSelectedVenues(prev => prev.filter((_, i) => i !== index));
    setComparison(null);
  };

  const handleCompare = async () => {
    if (selectedVenues.length < 2) {
      toast.error('Add at least 2 venues to compare');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/venue/compare`, {
        report_ids: selectedVenues.map(v => v.id)
      });
      setComparison(response.data.comparison_summary);
    } catch (error) {
      console.error('Compare error:', error);
      // Still show comparison from local data
      const summary = {
        highest_rated: selectedVenues.reduce((a, b) => (a.google_rating || 0) > (b.google_rating || 0) ? a : b).venue_name,
        best_value: selectedVenues.reduce((a, b) => (a.wvc_score?.value_score || 0) > (b.wvc_score?.value_score || 0) ? a : b).venue_name,
        best_hospitality: selectedVenues.reduce((a, b) => (a.wvc_score?.hospitality_score || 0) > (b.wvc_score?.hospitality_score || 0) ? a : b).venue_name,
        highest_wvc_score: selectedVenues.reduce((a, b) => (a.wvc_score?.total || 0) > (b.wvc_score?.total || 0) ? a : b).venue_name
      };
      setComparison(summary);
    }
  };

  const getCategoryScore = (venue, category) => {
    const rating = venue.category_ratings?.find(r => r.category === category);
    return rating?.score || '-';
  };

  const categories = [
    "Team & Responsiveness",
    "Hospitality & Staff Behavior",
    "Venue Ambience",
    "Cleanliness",
    "Food Quality",
    "Value for Money",
    "Parking & Accessibility",
    "Transparency"
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8] noise-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-stone-600 hover:text-stone-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-stone-900 rounded-full flex items-center justify-center">
                <span className="text-white font-serif text-sm font-bold">W</span>
              </div>
              <span className="font-serif text-xl font-medium text-stone-900">WVC Compare</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Venue Form */}
        <section className="mb-8">
          <h1 className="font-serif text-3xl font-medium text-stone-900 mb-6">
            Compare Wedding Venues
          </h1>
          
          <div className="max-w-2xl">
            <div className="relative">
              <GooglePlacesAutocomplete
                key={autocompleteKey}
                onPlaceSelect={handlePlaceSelect}
                onManualEntry={handleManualEntry}
                placeholder="Search venue by name or location..."
                disabled={isSearching || selectedVenues.length >= 4}
                showSelectedDetails={false}
                allowManualEntry={true}
                className="w-full"
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin" />
                </div>
              )}
            </div>
            <p className="text-sm text-stone-500 mt-3">
              Add 2-4 venues to compare • {selectedVenues.length}/4 added
            </p>
          </div>
        </section>

        {/* Selected Venues */}
        {selectedVenues.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-medium text-stone-900">Selected Venues</h2>
              {selectedVenues.length >= 2 && (
                <Button onClick={handleCompare} className="btn-secondary" data-testid="compare-btn">
                  Generate Comparison
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {selectedVenues.map((venue, index) => (
                <div key={index} className="card-base relative group" data-testid={`selected-venue-${index}`}>
                  <button
                    onClick={() => handleRemoveVenue(index)}
                    className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-venue-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <h3 className="font-medium text-stone-900 pr-8 line-clamp-2">{venue.venue_name}</h3>
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                    <span className="text-sm font-medium">{venue.google_rating}</span>
                    <span className="text-xs text-stone-400">({venue.total_reviews} reviews)</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-stone-500">WVC Score</span>
                    <span className="font-serif text-xl font-medium text-stone-900">{venue.wvc_score?.total || '-'}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comparison Summary */}
        {comparison && selectedVenues.length >= 2 && (
          <section className="mb-8 animate-fade-in">
            <h2 className="font-serif text-xl font-medium text-stone-900 mb-4">Quick Verdict</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card-base text-center">
                <Trophy className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <p className="data-label mb-1">Highest Rated</p>
                <p className="font-medium text-stone-900 text-sm">{comparison.highest_rated}</p>
              </div>
              <div className="card-base text-center">
                <Award className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="data-label mb-1">Best WVC Score</p>
                <p className="font-medium text-stone-900 text-sm">{comparison.highest_wvc_score}</p>
              </div>
              <div className="card-base text-center">
                <Heart className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                <p className="data-label mb-1">Best Hospitality</p>
                <p className="font-medium text-stone-900 text-sm">{comparison.best_hospitality}</p>
              </div>
              <div className="card-base text-center">
                <IndianRupee className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="data-label mb-1">Best Value</p>
                <p className="font-medium text-stone-900 text-sm">{comparison.best_value}</p>
              </div>
            </div>
          </section>
        )}

        {/* Side by Side Comparison Table */}
        {selectedVenues.length >= 2 && (
          <section className="animate-fade-in">
            <h2 className="font-serif text-xl font-medium text-stone-900 mb-4">Detailed Comparison</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-4 font-medium text-stone-600 min-w-[180px]">Metric</th>
                    {selectedVenues.map((venue, index) => (
                      <th key={index} className="text-center py-3 px-4 font-medium text-stone-900 min-w-[200px]">
                        <div className="line-clamp-2">{venue.venue_name}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* WVC Score Row */}
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <td className="py-4 px-4 font-medium text-stone-700">WVC Score</td>
                    {selectedVenues.map((venue, index) => (
                      <td key={index} className="py-4 px-4 text-center">
                        <div className="flex justify-center">
                          <ScoreCircle score={venue.wvc_score?.total || 0} />
                        </div>
                      </td>
                    ))}
                  </tr>
                  
                  {/* Google Rating */}
                  <tr className="border-b border-stone-100">
                    <td className="py-3 px-4 text-stone-600">Google Rating</td>
                    {selectedVenues.map((venue, index) => (
                      <td key={index} className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                          <span className="font-medium">{venue.google_rating}</span>
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Total Reviews */}
                  <tr className="border-b border-stone-100">
                    <td className="py-3 px-4 text-stone-600">Total Reviews</td>
                    {selectedVenues.map((venue, index) => (
                      <td key={index} className="py-3 px-4 text-center font-medium">
                        {venue.total_reviews?.toLocaleString()}
                      </td>
                    ))}
                  </tr>

                  {/* Category Ratings */}
                  {categories.map((category, catIndex) => (
                    <tr key={catIndex} className="border-b border-stone-100">
                      <td className="py-3 px-4 text-stone-600">{category}</td>
                      {selectedVenues.map((venue, index) => {
                        const score = getCategoryScore(venue, category);
                        const isHighest = selectedVenues.every(v => getCategoryScore(v, category) <= score);
                        return (
                          <td key={index} className="py-3 px-4 text-center">
                            <span className={`font-medium ${isHighest && score !== '-' ? 'text-emerald-600' : 'text-stone-900'}`}>
                              {score}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Best Highlights */}
                  <tr className="border-b border-stone-100 bg-emerald-50/50">
                    <td className="py-4 px-4 font-medium text-emerald-700">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Top Highlight
                      </div>
                    </td>
                    {selectedVenues.map((venue, index) => (
                      <td key={index} className="py-4 px-4 text-center text-sm text-stone-600">
                        {venue.best_highlights?.[0] || '-'}
                      </td>
                    ))}
                  </tr>

                  {/* Biggest Concern */}
                  <tr className="border-b border-stone-100 bg-red-50/50">
                    <td className="py-4 px-4 font-medium text-red-700">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        Top Concern
                      </div>
                    </td>
                    {selectedVenues.map((venue, index) => (
                      <td key={index} className="py-4 px-4 text-center text-sm text-stone-600">
                        {venue.biggest_concerns?.[0] || '-'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Empty State */}
        {selectedVenues.length === 0 && (
          <div className="text-center py-16">
            <Shield className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-serif text-xl text-stone-600 mb-2">No venues added yet</h3>
            <p className="text-stone-400">Search and add venues above to start comparing</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ComparePage;
